import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { atom, selector, useSetRecoilState } from 'recoil';
import {
  HeaderMenuItem,
  MenuItem,
  MenuVisibility,
  SidebarMenuItem,
} from './menu.types';
import { warehouseSelected } from './warehouse.state';
import { WarehouseExtraSettings } from './warehouse.types';

// Default menu items configuration
export const defaultMenuItems = (t: any): MenuItem[] => [
  {
    id: 'dashboard',
    title: t('Dashboards', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/dashboards`,
    default: false,
    beta: false,
    disabled: false,
    order: 1,
    isExact: true,
    visibility: MenuVisibility.BOTH,
    featureKey: 'dashboards',
    requiresFeature: false,
  },
  {
    id: 'schedule',
    title: t('Schedule', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/schedule`,
    default: false,
    beta: true,
    disabled: false,
    order: 2,
    isExact: true,
    visibility: MenuVisibility.BOTH,
    featureKey: 'schedule',
    requiresFeature: true,
  },
  {
    id: 'activity',
    title: t('Activity', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/feed/`,
    default: true,
    beta: false,
    disabled: false,
    order: 3,
    isExact: true,
    visibility: MenuVisibility.BOTH,
    featureKey: 'liveFeed',
    requiresFeature: true,
  },
  {
    id: 'simulations',
    title: t('Simulations', { ns: 'app' }),
    subtitle: t('Digital Twin', { ns: 'app' }),
    to: `/simulations`,
    default: true,
    beta: false,
    disabled: false,
    order: 4,
    isExact: true,
    visibility: MenuVisibility.BOTH,
    featureKey: 'simulations',
    requiresFeature: false,
  },
  {
    id: 'data',
    title: t('Data', { ns: 'app' }),
    subtitle: t('Warehouse Datasets', { ns: 'app' }),
    to: `/data`,
    default: true,
    beta: false,
    disabled: false,
    order: 5,
    isExact: false,
    visibility: MenuVisibility.BOTH,
    featureKey: 'data',
    requiresFeature: false,
  },
  {
    id: 'designer',
    title: t('Designer', { ns: 'app' }),
    subtitle: t('Warehouse Layout', { ns: 'app' }),
    to: `/designer`,
    default: false,
    beta: false,
    disabled: false,
    order: 7,
    isExact: false,
    visibility: MenuVisibility.BOTH,
    featureKey: 'designer',
    requiresFeature: false,
  },
  {
    id: 'query',
    title: t('Query', { ns: 'app' }),
    subtitle: t('Warehouse Datasets', { ns: 'app' }),
    to: `/query`,
    default: false,
    beta: false,
    disabled: false,
    order: 6,
    isExact: true,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'data',
    requiresFeature: true,
  },
    {
    id: 'automation',
    title: t('Data Automation', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/processing`,
    default: false,
    beta: false,
    disabled: false,
    order: 10,
    isExact: false,
    visibility: MenuVisibility.SIDEBAR,
    featureKey:  'data',
    requiresFeature: false,
  },


  {
    id: 'settings',
    title: t('Settings', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/edit`,
    default: true,
    beta: false,
    disabled: false,
    order: 10,
    isExact: false,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'settings',
    requiresFeature: false,
  },


  {
    id: 'billingStatus',
    title: t('Billing Status', { ns: 'app' }),
    subtitle: t('Account Settings', { ns: 'app' }),
    to: `/settings`,
    default: true,
    beta: false,
    disabled: false,
    collapsed: 'sm',
    order: 11,
    isExact: false,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'billing',
    requiresFeature: false,
  },
  {
    id: 'plansPricing',
    title: t('Plans & Pricing', { ns: 'app' }),
    subtitle: t('Account Settings', { ns: 'app' }),
    to: `/pricing`,
    default: false, // TODO: show only if no billing
    beta: false,
    disabled: false,
    collapsed: 'sm',
    order: 12,
    isExact: false,
    showIfActive: true,
    isPurchasing: true,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'billing',
    requiresFeature: false,
    widthCustom: 'max-w-10 min-w-10',
  },
  {
    id: 'wms-configurator',
    title: t('WMS Configurator', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/configurator`,
    default: false,
    beta: true,
    disabled: false,
    collapsed: 'sm',
    order: 21,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'wms-configurator',
    requiresFeature: false,
  },
  {
    id: 'warehouse-billing',
    title: t('Billing', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/billing`,
    default: false,
    beta: true,
    disabled: false,
    collapsed: 'sm',
    order: 21,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'warehouse-billing',
    requiresFeature: false,
  },
  {
    id: 'contracts',
    title: t('Contracts', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/contracts`,
    default: false,
    beta: true,
    disabled: false,
    collapsed: 'sm',
    order: 22,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'contracts',
    requiresFeature: false,
  },
  {
    id: 'marketplace',
    title: t('Marketplace', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/marketplace`,
    default: false,
    beta: true,
    disabled: false,
    collapsed: 'sm',
    order: 23,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'marketplace',
    requiresFeature: false,
  },
  {
    id: 'sales',
    title: t('Sales', { ns: 'app' }),
    subtitle: t('Warehouse', { ns: 'app' }),
    to: `/sales`,
    default: false,
    beta: true,
    disabled: true,
    collapsed: 'sm',
    order: 24,
    visibility: MenuVisibility.SIDEBAR,
    featureKey: 'sales',
    requiresFeature: false,
  },
];

// Atom for storing menu items
export const menuItemsState = atom<MenuItem[]>({
  key: 'menuItemsState',
  default: [],
});

// Core features that should always be enabled by default
export const CORE_FEATURES = ['simulations', 'data', 'designer'];

// Initialize feature toggles with defaults
const initializeFeatureToggles = (
  menuItems: MenuItem[],
): Record<string, boolean> => {
  const defaultToggles: Record<string, boolean> = {};

  // Always enable core features
  CORE_FEATURES.forEach(feature => {
    defaultToggles[feature] = true;
  });

  // Set additional defaults based on menu definition
  menuItems.forEach(item => {
    if (item.featureKey) {
      // Only enable if it's a core feature or specified as default
      defaultToggles[item.featureKey] =
        CORE_FEATURES.includes(item.featureKey) || !!item.default;
    }
  });

  return defaultToggles;
};

// Initialize menu item visibility with defaults
const initializeItemVisibility = (
  menuItems: MenuItem[],
): Record<string, boolean> => {
  const defaultVisibility: Record<string, boolean> = {};
  menuItems.forEach(item => {
    // Only set to true if it's a default item or has a core feature
    const isCore = item.featureKey
      ? CORE_FEATURES.includes(item.featureKey)
      : false;
    defaultVisibility[item.id] = isCore || !!item.default;
  });
  return defaultVisibility;
};

// Atom for storing feature toggle states (temporary)
export const featureToggleState = atom<Record<string, boolean>>({
  key: 'featureToggleState',
  default: {
    // Core features are always enabled by default
    simulations: true,
    data: true,
    designer: true,
  },
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization
      const savedToggles = localStorage.getItem('warebee_feature_toggles');
      if (savedToggles) {
        try {
          const parsedToggles = JSON.parse(savedToggles);
          // Ensure core features remain enabled
          setSelf({
            ...parsedToggles,
            simulations: true,
            data: true,
            designer: true,
          });
        } catch (error) {
          console.error(
            'Error loading feature toggles from localStorage:',
            error,
          );
        }
      }

      // Save to localStorage when changed, ensuring core features remain enabled
      onSet(newValue => {
        const valueToSave = {
          ...newValue,
          // Always ensure core features are enabled
          simulations: true,
          data: true,
          designer: true,
        };
        localStorage.setItem(
          'warebee_feature_toggles',
          JSON.stringify(valueToSave),
        );
      });
    },
  ],
});

// Atom for storing individual menu item visibility
export const menuItemVisibilityState = atom<Record<string, boolean>>({
  key: 'menuItemVisibilityState',
  default: {},
  effects: [
    ({ setSelf, onSet }) => {
      // Load from localStorage on initialization
      const savedVisibility = localStorage.getItem('warebee_item_visibility');
      if (savedVisibility) {
        try {
          setSelf(JSON.parse(savedVisibility));
        } catch (error) {
          console.error(
            'Error loading item visibility from localStorage:',
            error,
          );
        }
      }

      // Save to localStorage when changed
      onSet(newValue => {
        localStorage.setItem(
          'warebee_item_visibility',
          JSON.stringify(newValue),
        );
      });
    },
  ],
});

// Selector to get filtered menu items for sidebar
export const sidebarMenuItemsState = selector({
  key: 'sidebarMenuItemsState',
  get: ({ get }) => {
    const allMenuItems = get(menuItemsState);
    const warehouse = get(warehouseSelected);
    const featureToggles = get(featureToggleState) || {};
    const itemVisibility = get(menuItemVisibilityState) || {};
    const extraSettings: WarehouseExtraSettings | undefined =
      warehouse?.extraSettings;

    return allMenuItems
      .filter(item => {
        // First check if the item should be visible in the sidebar
        if (
          ![MenuVisibility.SIDEBAR, MenuVisibility.BOTH].includes(
            item.visibility as MenuVisibility,
          )
        ) {
          return false;
        }

        // Core features are always visible
        if (item.featureKey && CORE_FEATURES.includes(item.featureKey)) {
          return true;
        }

        // Check if the item is enabled in visibility settings
        const isVisible = itemVisibility[item.id];
        if (isVisible === false) {
          return false;
        }

        // If no visibility setting exists, use the default property
        if (isVisible === undefined && !item.default) {
          return false;
        }

        // Check feature toggles for non-core features
        if (item.featureKey && !CORE_FEATURES.includes(item.featureKey)) {
          if (featureToggles[item.featureKey] === false) {
            return false;
          }

          if (item.requiresFeature) {
            const featureSettings =
              extraSettings?.[item.featureKey as keyof WarehouseExtraSettings];
            if (!featureSettings || !(featureSettings as any)?.enabled) {
              return false;
            }
          }
        }

        return true;
      })
      .sort((a, b) => a.order - b.order) as SidebarMenuItem[];
  },
});

// Selector to get filtered menu items for header
export const headerMenuItemsState = selector({
  key: 'headerMenuItemsState',
  get: ({ get }) => {
    const allMenuItems = get(menuItemsState);
    const warehouse = get(warehouseSelected);
    const featureToggles = get(featureToggleState) || {};
    const itemVisibility = get(menuItemVisibilityState) || {};
    const extraSettings: WarehouseExtraSettings | undefined =
      warehouse?.extraSettings;

    return allMenuItems
      .filter(item => {
        // First check if the item should be visible in the header
        if (
          ![MenuVisibility.HEADER, MenuVisibility.BOTH].includes(
            item.visibility as MenuVisibility,
          )
        ) {
          return false;
        }

        // Core features are always visible
        if (item.featureKey && CORE_FEATURES.includes(item.featureKey)) {
          return true;
        }

        // Check if the item is enabled in visibility settings
        const isVisible = itemVisibility[item.id];
        if (isVisible === false) {
          return false;
        }

        // If no visibility setting exists, use the default property
        if (isVisible === undefined && !item.default) {
          return false;
        }

        // Check feature toggles for non-core features
        if (item.featureKey && !CORE_FEATURES.includes(item.featureKey)) {
          if (featureToggles[item.featureKey] === false) {
            return false;
          }

          if (item.requiresFeature) {
            const featureSettings =
              extraSettings?.[item.featureKey as keyof WarehouseExtraSettings];
            if (!featureSettings || !(featureSettings as any)?.enabled) {
              return false;
            }
          }
        }

        return true;
      })
      .sort((a, b) => a.order - b.order) as HeaderMenuItem[];
  },
});

// Hook to initialize menu items with translations
export const useInitializeMenuItems = () => {
  const { t } = useTranslation('app');
  const setMenuItems = useSetRecoilState(menuItemsState);
  const setFeatureToggles = useSetRecoilState(featureToggleState);
  const setItemVisibility = useSetRecoilState(menuItemVisibilityState);

  useEffect(() => {
    const items = defaultMenuItems(t);
    setMenuItems(items);

    // Initialize feature toggles and item visibility with defaults
    const defaultToggles = initializeFeatureToggles(items);
    const defaultVisibility = initializeItemVisibility(items);

    // Always set the core features to true
    const initialFeatureToggles = {
      ...defaultToggles,
      // Ensure core features are always enabled
      simulations: true,
      data: true,
      designer: true,
    };

    // Set initial state
    setFeatureToggles(initialFeatureToggles);
    setItemVisibility(defaultVisibility);

    // Save to localStorage if not already set
    if (!localStorage.getItem('warebee_feature_toggles')) {
      localStorage.setItem(
        'warebee_feature_toggles',
        JSON.stringify(initialFeatureToggles),
      );
    }
    if (!localStorage.getItem('warebee_item_visibility')) {
      localStorage.setItem(
        'warebee_item_visibility',
        JSON.stringify(defaultVisibility),
      );
    }
  }, [t, setMenuItems, setFeatureToggles, setItemVisibility]);

  return defaultMenuItems(t);
};

// Separate hook for initializing menu state
export const useInitializeMenuState = () => {
  const { t } = useTranslation('app');
  const setMenuItems = useSetRecoilState(menuItemsState);
  const setFeatureToggles = useSetRecoilState(featureToggleState);
  const setItemVisibility = useSetRecoilState(menuItemVisibilityState);

  useEffect(() => {
    const items = defaultMenuItems(t);
    setMenuItems(items);

    // Initialize feature toggles and item visibility with defaults
    const defaultToggles = initializeFeatureToggles(items);
    const defaultVisibility = initializeItemVisibility(items);

    // Always set the core features to true
    const initialFeatureToggles = {
      ...defaultToggles,
      // Ensure core features are always enabled
      simulations: true,
      data: true,
      designer: true,
    };

    // Set initial state
    setFeatureToggles(initialFeatureToggles);
    setItemVisibility(defaultVisibility);

    // Save to localStorage if not already set
    if (!localStorage.getItem('warebee_feature_toggles')) {
      localStorage.setItem(
        'warebee_feature_toggles',
        JSON.stringify(initialFeatureToggles),
      );
    }
    if (!localStorage.getItem('warebee_item_visibility')) {
      localStorage.setItem(
        'warebee_item_visibility',
        JSON.stringify(defaultVisibility),
      );
    }
  }, [t, setMenuItems, setFeatureToggles, setItemVisibility]);
};
